import { STATIONS } from "@frontier/content/stations";
import { factionRegionNumber } from "@frontier/content/faction-routes";
import { FACTIONS, FACTION_IDS } from "@frontier/content/factions";
import { REGIONS } from "@frontier/content/world-layout";
import { regionRisk } from "@frontier/content/risk";
import type { WorldSnapshot } from "@frontier/protocol/contracts";
import { buildGalaxyGraph, galaxyRegionAt } from "./galaxy-navigation";
import type { GalaxyTarget } from "./galaxy-navigation";
import { STATION_CHART_POSITIONS, GALAXY_CHART_SIZE, GALAXY_FACTION_HEADINGS, GALAXY_REGION_POSITIONS, galaxyLinkPath } from "./galaxy-layout";
import { GALAXY_REGION_ART } from "./galaxy-art";
import type { GalaxyView } from "./galaxy-viewport";

interface Props {
  world: WorldSnapshot;
  contracts: readonly { sectorId: string }[];
  currentRegionId: string | null;
  docked: boolean;
  selected: GalaxyTarget | null;
  onSelect: (target: GalaxyTarget) => void;
  view?: GalaxyView;
  onPreview?: (target: GalaxyTarget) => void;
  onLeave?: () => void;
  onFocus?: (target: GalaxyTarget) => void;
  popupId?: string;
  pinned?: boolean;
}

export function GalaxyMapGraph({
  world,
  contracts,
  currentRegionId,
  docked,
  selected,
  onSelect,
  view = { x: 0, y: 0, scale: 1 },
  onPreview,
  onLeave,
  onFocus,
  popupId,
  pinned = false,
}: Props) {
  const nodes = REGIONS.filter((definition) => definition.id !== "training").flatMap((definition) => {
    const region = world.regions.find((item) => item.id === definition.id);
    return region ? [{ region, art: GALAXY_REGION_ART[definition.id], ...GALAXY_REGION_POSITIONS[definition.id] }] : [];
  });
  const edges = buildGalaxyGraph(world);
  const stations = STATIONS.flatMap((station) => {
    const node = nodes.find((item) => item.region.id === station.sectorId);
    return node ? [{ station, node, position: STATION_CHART_POSITIONS[station.id] }] : [];
  });
  const stationSelected = (id: string) => selected?.kind === "station" && (selected.id ?? "meridian") === id;
  return (
    <div
      className="galaxy-map-graph"
      aria-label="Схема известных областей и переходов"
    >
      <div className="galaxy-map-chart" style={{ width: GALAXY_CHART_SIZE.width, height: GALAXY_CHART_SIZE.height, transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})` }}>
      <svg
        className="galaxy-map-links"
        viewBox="0 0 1000 1000"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <marker
            id="galaxy-gate-arrow"
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path d="M0 0 10 5 0 10Z" />
          </marker>
        </defs>
        {stations.map(({ station, node, position }) => <path
          key={station.id} className="galaxy-map-station-link"
          d={`M${node.x * 10} ${node.y * 10} L${position.x * 10} ${position.y * 10}`}
          data-station-link={station.sectorId}
        />)}
        {edges.map((edge) => {
          const from = nodes.find(
            (node) => node.region.id === edge.fromSectorId,
          );
          const to = nodes.find((node) => node.region.id === edge.toSectorId);
          if (!from || !to) return null;
          return (
            <path
              key={`${edge.fromSectorId}:${edge.toSectorId}`}
              d={galaxyLinkPath(edge.fromSectorId, edge.toSectorId, from, to)}
              className="galaxy-map-link"
              data-from-sector={edge.fromSectorId}
              data-to-sector={edge.toSectorId}
              data-gate-ids={edge.gateIds.join(" ")}
              markerEnd="url(#galaxy-gate-arrow)"
            />
          );
        })}
      </svg>
      <span className="galaxy-shared-heading">ОБЩИЕ ОБЛАСТИ · МЕРИДИАН</span>
      {FACTION_IDS.map((id) => (
        <span key={id} className="galaxy-faction-heading" style={{ left: `${GALAXY_FACTION_HEADINGS[id].x}%`, top: `${GALAXY_FACTION_HEADINGS[id].y}%`, color: FACTIONS[id].color }}>
          {FACTIONS[id].name}
        </span>
      ))}
      {nodes.map(({ region, art, x, y }) => {
        const risk = regionRisk(region.id);
        const factionId = REGIONS.find((item) => item.id === region.id)?.factionId;
        const terminal = !!factionId && new Set(edges.filter((edge) => edge.fromSectorId === region.id && edge.toSectorId !== region.id).map((edge) => edge.toSectorId)).size === 1;
        const chosen = selected?.kind === "region" && selected.id === region.id;
        const current = currentRegionId === region.id;
        const contractCount = contracts.filter(
          (contract) => contract.sectorId === region.id,
        ).length;
        const events = world.publicEvents.filter(
          (event) =>
            event.sectorId === region.id &&
            (event.status === "available" || event.status === "active"),
        ).length;
        return (
          <div
            key={region.id}
            className={`galaxy-map-region ${risk}`}
            style={{ left: `${x}%`, top: `${y}%` }}
            data-faction-id={factionId ?? "shared"}
            data-terminal={terminal || undefined}
          >
            {stations.some(({ station }) => station.sectorId === region.id) && (
              <span className="galaxy-station-orbit" aria-hidden="true" />
            )}
            <button
              type="button"
              className={`galaxy-region-button${chosen ? " selected" : ""}${current ? " current" : ""}`}
              aria-label={`Область: ${region.name}`}
              aria-pressed={chosen}
              data-galaxy-focus
              data-region-id={region.id}
              aria-describedby={chosen && !pinned ? popupId : undefined}
              aria-controls={chosen && pinned ? popupId : undefined}
              aria-expanded={chosen && pinned}
              onPointerEnter={(event) => { if (event.pointerType !== "touch" && event.buttons === 0) onPreview?.({ kind: "region", id: region.id }); }}
              onPointerLeave={onLeave}
              onFocus={() => onFocus?.({ kind: "region", id: region.id })}
              onClick={() => onSelect({ kind: "region", id: region.id })}
            >
              <span className={`galaxy-region-planet galaxy-art-${art.kind}`} title={art.description}>
                <img src={art.asset} alt="" draggable={false} />
                {art.detail && <img className="galaxy-art-detail" src={art.detail} alt="" draggable={false} />}
              </span>
              <span className="galaxy-region-name">{factionRegionNumber(region.id) ? `${factionRegionNumber(region.id)} · ` : ""}{region.name}</span>
              {current && (
                <span className="galaxy-current-label">ТЫ ЗДЕСЬ</span>
              )}
              {(contractCount > 0 || events > 0) && (
                <span className="galaxy-node-counts">
                  {contractCount > 0 && (
                    <span title="Принятые задания">◇ {contractCount}</span>
                  )}
                  {events > 0 && (
                    <span title="Публичные события">✦ {events}</span>
                  )}
                </span>
              )}
            </button>
          </div>
        );
      })}
      {stations.map(({ station, position, node }) => (
        <button key={station.id}
          type="button"
          className={`galaxy-station-button${stationSelected(station.id) ? " selected" : ""}${docked && currentRegionId === station.sectorId ? " current" : ""}`}
          style={{ left: `${position.x}%`, top: `${position.y}%` }}
          aria-label={`Станция ${station.name}, ${node.region.name}`}
          aria-pressed={stationSelected(station.id)} data-station-region={station.sectorId}
          data-galaxy-focus
          aria-describedby={stationSelected(station.id) && !pinned ? popupId : undefined}
          aria-controls={stationSelected(station.id) && pinned ? popupId : undefined}
          aria-expanded={stationSelected(station.id) && pinned}
          onPointerEnter={(event) => { if (event.pointerType !== "touch" && event.buttons === 0) onPreview?.({ kind: "station", id: station.id }); }}
          onPointerLeave={onLeave}
          onFocus={() => onFocus?.({ kind: "station", id: station.id })}
          onClick={() => onSelect({ kind: "station", id: station.id })}
        >
          <img src="/assets/station.png" alt="" draggable={false} />
          <span>{station.name.toUpperCase()}</span>
        </button>
      ))}
      </div>
      <div className="galaxy-map-legend" aria-hidden="true">
        <i /> ПЕРЕХОДЫ ЧЕРЕЗ ВРАТА
      </div>
    </div>
  );
}
